import { gql } from "@apollo/client";
import { useMutation } from "@apollo/client/react";
import { notifications } from "@mantine/notifications";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import { CreationForm } from "../../../components/form/CreationForm";
import { PageHeader } from "../../../components/PageHeader";
import { OptionsEditor } from "../shared/OptionsEditor";
import { useAllowedObjectTypes } from "../shared/objectTypeOptions";
import {
  CUSTOM_FIELD_DEFINITION_FRAGMENT,
  CUSTOM_FIELD_TYPE_OPTIONS,
  CustomFieldType,
  EDITABILITY_OPTIONS,
  VISIBILITY_OPTIONS,
} from "../shared/customFieldFragments";

const CREATE_DEFINITION = gql`
  mutation createCustomFieldDefinition(
    $input: CreateCustomFieldDefinitionInput!
  ) {
    createCustomFieldDefinition(input: $input) {
      ...CustomFieldDefinitionFields
    }
  }
  ${CUSTOM_FIELD_DEFINITION_FRAGMENT}
`;

export const CustomFieldDefinitionCreate = () => {
  const navigate = useNavigate();
  const { options: objectTypeOptions } = useAllowedObjectTypes();
  const [createMutation] = useMutation(CREATE_DEFINITION);
  const [selectedType, setSelectedType] = useState<string>("");

  const showOptions =
    selectedType === CustomFieldType.SELECT ||
    selectedType === CustomFieldType.MULTI_SELECT;

  return (
    <div className="wp-fade-in">
      <PageHeader
        title="Create Custom Field Definition"
        showBackButton
        showBreadcrumbs={false}
      />
      <CreationForm
        title="New Custom Field"
        schemaDefinition={{
          objectType: {
            type: "SELECT",
            label: "Object Type",
            yupConfig: yup.string().required("Object type is required"),
            options: objectTypeOptions,
          },
          key: {
            type: "INPUT",
            label: "Key",
            yupConfig: yup
              .string()
              .required("Key is required")
              .matches(
                /^[a-zA-Z0-9_-]{1,100}$/,
                "Key must be alphanumeric with hyphens/underscores, 1-100 chars",
              ),
            inputType: "text",
            placeholder: "e.g. discord_username",
            subtext:
              "Unique identifier for this field (cannot be changed later)",
          },
          name: {
            type: "INPUT",
            label: "Name",
            yupConfig: yup.string().required("Name is required").max(200),
            inputType: "text",
            placeholder: "e.g. Discord Username",
          },
          description: {
            type: "TEXTAREA",
            label: "Description",
            yupConfig: yup.string().max(1024).nullable(),
          },
          type: {
            type: "SELECT",
            label: "Field Type",
            yupConfig: yup.string().required("Field type is required"),
            options: CUSTOM_FIELD_TYPE_OPTIONS,
            onChange(value: string) {
              setSelectedType(value);
            },
          },
          required: {
            type: "CHECKBOX",
            label: "Required",
            yupConfig: yup.boolean(),
            defaultValue: false,
          },
          unique: {
            type: "CHECKBOX",
            label: "Unique",
            yupConfig: yup.boolean(),
            defaultValue: false,
          },
          visibility: {
            type: "SELECT",
            label: "Visibility",
            yupConfig: yup.string().required("Visibility is required"),
            options: VISIBILITY_OPTIONS,
            defaultValue: "PUBLIC",
          },
          editability: {
            type: "SELECT",
            label: "Editability",
            yupConfig: yup.string().required("Editability is required"),
            options: EDITABILITY_OPTIONS,
            defaultValue: "ALWAYS",
          },
          ...(showOptions && {
            options: {
              type: "DYNAMIC" as const,
              label: "Options",
              yupConfig: yup
                .array()
                .max(200, "Maximum 200 options allowed")
                .of(
                  yup.object({
                    label: yup
                      .string()
                      .required("Label is required")
                      .max(200, "Label too long"),
                    value: yup
                      .string()
                      .required("Value is required")
                      .max(200, "Value too long"),
                  }),
                ),
              element: (onChange: (...args: any[]) => void) => (
                <OptionsEditor onChange={onChange} initializeEmpty />
              ),
            },
          }),
          defaultValue: {
            type: "INPUT",
            label: "Default Value",
            yupConfig: yup.string().max(4096).nullable(),
            inputType: "text",
            placeholder: "Optional default value",
          },
          validationRegex: {
            type: "INPUT",
            label: "Validation Regex",
            yupConfig: yup
              .string()
              .max(500)
              .nullable()
              .test(
                "valid-regex",
                "Must be a valid regular expression without nested quantifiers",
                (value) => {
                  if (!value) return true;
                  try {
                    new RegExp(value);
                    // Reject patterns with nested quantifiers (ReDoS risk)
                    if (/(\(.*[+*].*\)[+*]|\.\*.*\.\*)/.test(value)) {
                      return false;
                    }
                    return true;
                  } catch {
                    return false;
                  }
                },
              ),
            inputType: "text",
            placeholder: "Optional regex pattern for validation",
          },
        }}
        onSubmit={async (values) => {
          const input = {
            objectType: values.objectType,
            key: values.key,
            name: values.name,
            description: values.description || undefined,
            type: values.type,
            required: values.required ?? false,
            unique: values.unique ?? false,
            visibility: values.visibility,
            editability: values.editability,
            options: values.options?.length > 0 ? values.options : undefined,
            defaultValue: values.defaultValue || undefined,
            validationRegex: values.validationRegex || undefined,
          };

          try {
            await createMutation({ variables: { input } }).then((result) => {
              if (result?.error) {
                throw new Error(result.error.message);
              }
            });

            notifications.show({
              title: "Field created",
              message: `Custom field "${values.name}" has been created`,
              color: "green",
              autoClose: 3000,
            });

            navigate("/data/custom-fields");
          } catch (error) {
            notifications.show({
              title: "Failed to create field",
              message:
                error instanceof Error && error.message.length < 200
                  ? error.message
                  : "An unexpected error occurred",
              color: "red",
              autoClose: 5000,
            });
          }
        }}
      />
    </div>
  );
};
